import { useEffect, useState } from "react";
import { Activity, Radio, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { Loading } from "./common";
import { Button } from "./ui/button";

const ago = (iso) => (iso ? `${Math.max(0, Math.round((Date.now() - new Date(iso)) / 60000))} min ago` : "never");
const tone = (st) => (!st ? "text-muted-foreground" : st < 400 ? "text-emerald-400" : "text-rose-400");

// Admin command center: every FiveM -> website route with its last real HTTP status.
export function GatewayHealth() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/virtucity/gateway-health").then(({ data }) => setD(data)).catch((e) => setD({ error: errText(e) }));
  useEffect(() => { load(); }, []);
  if (!d) return <Loading />;
  if (d.error) return <div className="text-sm text-rose-400" data-testid="gateway-health-error">{d.error}</div>;
  return (
    <div className="surface-2 rounded-xl p-4" data-testid="gateway-health">
      <div className="flex items-center gap-2 mb-3">
        <Activity size={16} className="text-eblue" /><div className="font-semibold text-sm flex-1">FiveM gateway status</div>
        <span className="text-xs text-muted-foreground" data-testid="gateway-pending-commands">Pending NPC commands: {d.pending_commands} · Unacked sync jobs: {d.pending_sync_jobs}</span>
        <button onClick={load} className="p-1.5 text-muted-foreground hover:text-white" data-testid="gateway-health-refresh"><RefreshCw size={14} /></button>
      </div>
      {d.routes.length === 0 ? <p className="text-xs text-muted-foreground">No requests from the game server yet.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-xs">
          <thead><tr className="text-muted-foreground text-left"><th className="py-1 pr-3">Resource</th><th className="pr-3">Route</th><th className="pr-3">Last</th><th className="pr-3">OK / Fail</th><th>Last failure</th></tr></thead>
          <tbody>{d.routes.map((r) => (
            <tr key={r.method + r.path} className="border-t border-white/5" data-testid={`gateway-route-${r.path.replace(/[^a-z0-9]+/gi, "-")}`}>
              <td className="py-1.5 pr-3 font-mono">{r.resource}</td><td className="pr-3 font-mono">{r.method} {r.path.replace("/api", "")}</td>
              <td className={`pr-3 font-mono ${tone(r.last_status)}`}>{r.last_status} · {ago(r.last_at)}</td>
              <td className="pr-3 font-mono">{r.ok || 0} / {r.fail || 0}</td>
              <td className="font-mono text-rose-300">{r.last_fail_status ? `${r.last_fail_status} · ${ago(r.last_fail_at)}` : "—"}</td>
            </tr>))}</tbody>
        </table></div>
      )}
      {d.recent_failures.length > 0 && <div className="mt-3 text-xs space-y-1" data-testid="gateway-recent-failures">
        <div className="text-muted-foreground">Recent reported failures</div>
        {d.recent_failures.slice(0, 8).map((f, i) => <div key={i} className="font-mono text-rose-300">{[f.kind, f.reason, f.extra?.detail, ago(f.at)].filter(Boolean).join(" · ")}</div>)}
      </div>}
    </div>
  );
}

export function RadioHealth() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/virtucity/radio/health").then(({ data }) => setD(data)).catch((e) => setD({ error: errText(e) }));
  useEffect(() => { load(); }, []);
  const recheck = async (id) => { try { await api.post("/admin/virtucity/radio/recheck", null, { params: id ? { track_id: id } : {} }); toast.success("Re-check queued"); setTimeout(load, 4000); } catch (e) { toast.error(errText(e)); } };
  if (!d) return <Loading />;
  if (d.error) return <div className="text-sm text-rose-400">{d.error}</div>;
  const pb = d.playback_24h || {};
  return (
    <div className="surface-2 rounded-xl p-4" data-testid="radio-health">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Radio size={16} className="text-gold" /><div className="font-semibold text-sm flex-1">VStars Radio audio check</div>
        <span className="text-xs" data-testid="radio-health-summary"><span className="text-emerald-400">{d.playable} playable</span> · {d.pending} checking · {d.tracks.length - d.playable - d.pending} rejected</span>
        <Button size="sm" variant="outline" className="h-7 rounded-lg border-white/15 text-xs" onClick={() => recheck()} data-testid="radio-recheck-failed">Re-check rejected</Button>
      </div>
      <div className="text-xs text-muted-foreground mb-2" data-testid="radio-playback-24h">In-game playback (24h, reported by players' game browser): playing {pb.playing || 0} · errors {pb.error || 0} · blocked {pb.blocked || 0} · stalled {pb.stalled || 0}. "Playing" means the game browser started the audio; it can't confirm a player's speakers.</div>
      <div className="max-h-56 overflow-y-auto space-y-1">
        {d.tracks.map((t) => (
          <div key={t.id} className="flex items-center gap-2 text-xs" data-testid={`radio-track-${t.id}`}>
            <span className={t.ok ? "text-emerald-400" : t.ok === false ? "text-rose-400" : "text-amber-300"}>{t.ok ? "OK" : t.ok === false ? "NO" : "…"}</span>
            <span className="flex-1 truncate">{t.title}</span>
            <span className="font-mono text-muted-foreground">{t.source_format || "?"}{t.reason === "transcoded_wav_to_mp3" ? "→mp3" : ""} · {t.duration ? `${Math.round(t.duration)}s` : "—"}{t.ok === false ? ` · ${t.reason}` : ""}</span>
            <button onClick={() => recheck(t.id)} className="text-muted-foreground hover:text-gold" data-testid={`radio-recheck-${t.id}`}><RefreshCw size={12} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
